<main class="automation-builder">
  <section class="canvas-shell" aria-label="Construtor de automacoes">
    <header class="workflow-toolbar" aria-label="Controles do workflow">
      <div class="workflow-toolbar__copy">
        <h1>{{ automationName() }}</h1>
        <p>Construtor de automação · {{ statusLabel() }}{{ dirty() ? ' · alterações não salvas' : '' }}</p>
      </div>

      <div class="workflow-toolbar__actions">
        <button appButton class="toolbar-add" [variant]="blockMenuOpen() ? 'primary' : 'secondary'" size="sm" type="button" (click)="toggleBlockMenu()">
          <ng-icon name="heroPlus" size="15" aria-hidden="true" />
          Adicionar bloco
        </button>
        <button appButton variant="ghost" size="sm" type="button" [disabled]="runningTest()" (click)="runTest()">
          <ng-icon name="heroPlayCircle" size="16" aria-hidden="true" />
          Testar
        </button>
        <button appButton variant="secondary" size="sm" type="button" [disabled]="saving()" (click)="saveDraft()">
          <ng-icon name="heroArrowPath" size="16" aria-hidden="true" />
          Salvar rascunho
        </button>
        <button appButton class="toolbar-activate" variant="primary" size="sm" type="button" [disabled]="saving()" (click)="activate()">Ativar</button>
      </div>
    </header>

    <f-flow
      fDraggable
      class="automation-flow"
      fFlowId="automation-builder-flow"
      (fCreateConnection)="createConnection($event)"
      (fReassignConnection)="reassignConnection($event)"
    >
      <f-background>
        <f-rect-pattern id="automation-grid" [hColor]="'var(--color-line)'" [vColor]="'var(--color-line)'" [hSize]="32" [vSize]="32" />
      </f-background>

      <f-canvas [position]="canvasPosition" [scale]="canvasScale">
        <f-connection-for-create
          [fType]="connectionType"
          [fBehavior]="connectionBehavior"
          [fInputSide]="inputSide"
          [fOutputSide]="outputSide"
        >
          <f-connection-marker-arrow [type]="markerEnd" />
        </f-connection-for-create>

        <f-snap-connection
          [fSnapThreshold]="56"
          [fType]="connectionType"
          [fBehavior]="connectionBehavior"
          [fInputSide]="inputSide"
          [fOutputSide]="outputSide"
        >
          <f-connection-marker-arrow [type]="markerEnd" />
        </f-snap-connection>

        @for (connection of connections(); track connection.id) {
          <f-connection
            [fConnectionId]="connection.id"
            [fOutputId]="connection.from"
            [fInputId]="connection.to"
            [fType]="connectionType"
            [fBehavior]="connectionBehavior"
            [fInputSide]="inputSide"
            [fOutputSide]="outputSide"
            [fReassignableStart]="true"
          >
            <f-connection-marker-arrow [type]="markerEnd" />
          </f-connection>
        }

        @for (node of nodes(); track node.id) {
          <article
            appUiPanelCard
            fNode
            fDragHandle
            [fNodeId]="node.id"
            [fNodePosition]="node.position"
            (fNodePositionChange)="moveNode(node.id, $event)"
            class="flow-node"
            [ngClass]="[
              kindClass(node.kind),
              selectedNodeId() === node.id ? 'flow-node--selected' : '',
              isNodeExpanded(node.id) ? 'flow-node--expanded' : '',
              issueForNode(node) ? 'flow-node--invalid' : ''
            ]"
            tabindex="0"
            [attr.aria-selected]="selectedNodeId() === node.id"
            [attr.aria-label]="'Bloco ' + node.title"
            (click)="selectNode(node.id)"
            (keydown.enter)="selectNode(node.id)"
            (keydown.space)="selectNodeFromKeyboard($event, node.id)"
          >
            @if (node.inputId) {
              <span
                fNodeInput
                class="flow-port flow-port--in"
                [attr.data-port-id]="node.inputId"
                [fInputId]="node.inputId"
                [fInputConnectableSide]="inputSide"
                aria-hidden="true"
              ></span>
            }

            <header class="flow-node__header">
              <span class="flow-node__icon" aria-hidden="true">
                <ng-icon [name]="node.icon" size="16" />
              </span>
              <span class="flow-node__heading">
                <span>{{ node.eyebrow }}</span>
                <strong>{{ node.title }}</strong>
              </span>
              <span class="flow-node__actions">
                <button
                  appUiIconButton
                  fDragBlocker
                  variant="danger"
                  size="sm"
                  type="button"
                  aria-label="Apagar bloco"
                  (click)="deleteNode(node.id, $event)"
                >
                  <ng-icon name="heroTrash" size="14" aria-hidden="true" />
                </button>
              </span>
            </header>

            <p class="flow-node__description">{{ node.description }}</p>

            @if (issueForNode(node); as issue) {
              <div class="flow-node__issue">
                <ng-icon name="heroExclamationTriangle" size="14" aria-hidden="true" />
                <span>{{ issue.message }}</span>
              </div>
            } @else if (previewField(node); as field) {
              <div class="flow-node__preview">
                <span>{{ field.label }}</span>
                <strong>{{ fieldDisplayValue(node, field) }}</strong>
              </div>
            }

            <footer class="flow-node__footer">
              @for (badge of node.badges; track badge) {
                <span appBadge size="xs" [tone]="badgeTone(badge)">{{ badge }}</span>
              }
              <button
                appButton
                fDragBlocker
                class="node-expand"
                variant="ghost"
                size="sm"
                type="button"
                [attr.aria-expanded]="isNodeExpanded(node.id)"
                (click)="toggleNodeExpanded(node.id, $event)"
              >
                <ng-icon name="heroAdjustmentsHorizontal" size="14" aria-hidden="true" />
                {{ isNodeExpanded(node.id) ? 'Recolher' : 'Configurar' }}
              </button>
            </footer>

            @if (isNodeExpanded(node.id)) {
              <section fDragBlocker class="node-config" aria-label="Configuracao do bloco">
                <div class="node-config__fields">
                  @for (field of visibleNodeFields(node); track field.key) {
                    <label class="node-field" [attr.for]="fieldControlId(node.id, field.key)">
                      <span appUiLabel>
                        {{ field.label }}
                        @if (field.required) {
                          <strong>*</strong>
                        }
                      </span>

                      @switch (field.type) {
                        @case ('textarea') {
                          <textarea
                            appTextarea
                            [id]="fieldControlId(node.id, field.key)"
                            [value]="field.value"
                            [placeholder]="field.placeholder ?? ''"
                            (input)="updateField(node.id, field.key, $event)"
                          ></textarea>
                        }
                        @case ('select') {
                          <select
                            appNativeSelect
                            [id]="fieldControlId(node.id, field.key)"
                            [value]="field.value"
                            (change)="updateField(node.id, field.key, $event)"
                          >
                            <option value="" [selected]="!field.value">Selecione</option>
                            @if (field.optionSource === 'channelAccounts' && fieldOptions(node, field).length === 0) {
                              <option value="" disabled>Nenhum canal ativo encontrado</option>
                            }
                            @for (option of fieldOptions(node, field); track option.value) {
                              <option [value]="option.value" [selected]="field.value === option.value">{{ option.label }}</option>
                            }
                          </select>
                        }
                        @case ('combobox') {
                          <input
                            appInput
                            [id]="fieldControlId(node.id, field.key)"
                            type="text"
                            [attr.list]="fieldOptionsId(node.id, field.key)"
                            [value]="field.value"
                            [placeholder]="field.placeholder ?? 'Digite ou selecione'"
                            (input)="updateField(node.id, field.key, $event)"
                          />
                          <datalist [id]="fieldOptionsId(node.id, field.key)">
                            @for (option of fieldOptions(node, field); track option.value) {
                              <option [value]="option.value" [label]="option.label"></option>
                            }
                          </datalist>
                        }
                        @case ('boolean') {
                          <button
                            appSwitch
                            type="button"
                            [ngpSwitchChecked]="field.value === 'true'"
                            (ngpSwitchCheckedChange)="updateBooleanField(node.id, field.key, $event)"
                            [attr.aria-label]="field.label"
                          >
                            <span appSwitchThumb></span>
                          </button>
                        }
                        @default {
                          <input
                            appInput
                            [id]="fieldControlId(node.id, field.key)"
                            [type]="fieldInputType(field)"
                            [value]="field.value"
                            [placeholder]="field.placeholder ?? ''"
                            (input)="updateField(node.id, field.key, $event)"
                          />
                        }
                      }

                      @if (field.optionSource === 'channelAccounts' && fieldOptions(node, field).length === 0) {
                        <small appUiHint>Conecte ou ative um canal antes de ativar a automacao.</small>
                      } @else if (field.helper) {
                        <small appUiHint>{{ field.helper }}</small>
                      } @else if (field.optionSource === 'businessFields') {
                        <small appUiHint>{{ fieldOptions(node, field).length }} campos disponiveis no contexto.</small>
                      }
                    </label>
                  }
                </div>

                <div class="node-config__routing">
                  <div>
                    <span>Entrada</span>
                    @if (incomingForNode(node); as incoming) {
                      <strong>{{ nodeTitleByOutput(incoming.from) }}</strong>
                      <button appButton variant="ghost" size="sm" type="button" (click)="disconnectNodeInput(node, $event)">Remover</button>
                    } @else {
                      <strong>{{ node.kind === 'trigger' ? 'Inicio do fluxo' : 'Sem entrada' }}</strong>
                    }
                  </div>

                  <div>
                    <span>Saidas</span>
                    @if (outgoingForNode(node).length > 0) {
                      @for (connection of outgoingForNode(node); track connection.id) {
                        <button appButton variant="ghost" size="sm" type="button" (click)="removeConnection(connection.id)">
                          {{ nodeTitleByInput(connection.to) }}
                        </button>
                      }
                    } @else {
                      <strong>{{ node.kind === 'end' ? 'Terminal' : 'Sem saidas' }}</strong>
                    }
                  </div>
                </div>

                <div class="node-config__contract" aria-label="Campos de entrada exigidos">
                  @for (field of requiredInputFields(node); track field) {
                    <span appBadge tone="info" size="xs">
                      <ng-icon name="heroAdjustmentsHorizontal" size="13" aria-hidden="true" />
                      {{ field }}
                    </span>
                  }
                  @for (field of outputFieldsForNode(node); track field) {
                    <span appBadge tone="neutral" size="xs">
                      <ng-icon name="heroCodeBracket" size="13" aria-hidden="true" />
                      {{ field }}
                    </span>
                  }
                </div>
              </section>
            }

            @if (node.outputId) {
              <span
                fNodeOutput
                class="flow-port flow-port--out"
                [attr.data-port-id]="node.outputId"
                [fOutputId]="node.outputId"
                [fOutputMultiple]="true"
                [fCanBeConnectedInputs]="connectableInputIds(node)"
                [fOutputConnectableSide]="outputSide"
                aria-hidden="true"
              ></span>
            }
          </article>
        }
      </f-canvas>
    </f-flow>

    <aside class="workflow-status" aria-label="Resumo e pendencias">
      <div appUiPanelCard class="workflow-status__stats">
        @for (item of flowSummary(); track item.label) {
          <span>
            <small>{{ item.label }}</small>
            <strong>{{ item.value }}</strong>
          </span>
        }
      </div>
      @if (validationIssues().length > 0) {
        <div appUiPanelCard class="workflow-status__issues">
          @for (issue of validationIssues().slice(0, 3); track issue.message) {
            <p [class.workflow-status__issue--warning]="issue.tone === 'warning'">
              <ng-icon name="heroExclamationTriangle" size="13" aria-hidden="true" />
              {{ issue.message }}
            </p>
          }
        </div>
      }
    </aside>

    @if (blockMenuOpen()) {
      <section appUiPanelCard class="block-menu" aria-label="Adicionar bloco ao workflow">
        <nav class="block-menu__types" aria-label="Tipos de bloco">
          @for (group of templateGroups; track group.id) {
            <button
              appUiOptionRow
              compact
              type="button"
              [class.block-menu__type--active]="selectedTemplateGroup() === group.id"
              [selected]="selectedTemplateGroup() === group.id"
              (click)="selectTemplateGroup(group.id)"
            >
              <ng-icon [name]="group.icon" size="16" aria-hidden="true" />
              <span>
                <strong>{{ group.label }}</strong>
                <small>{{ templateGroupCount(group.id) }}</small>
              </span>
            </button>
          }
        </nav>

        <div class="block-menu__content">
          <div class="block-menu__topline">
            @for (group of templateGroups; track group.id) {
              @if (selectedTemplateGroup() === group.id) {
                <span>
                  <strong>{{ group.label }}</strong>
                  <small>{{ group.description }}</small>
                </span>
              }
            }
            <button appButton variant="ghost" size="sm" type="button" (click)="closeBlockMenu()">Fechar</button>
          </div>

          <label class="block-search">
            <ng-icon name="heroAdjustmentsHorizontal" size="15" aria-hidden="true" />
            <input
              appInput
              leadingIcon
              surface="transparent"
              type="search"
              placeholder="Buscar opcao"
              [value]="librarySearch()"
              (input)="setLibrarySearch($event)"
            />
          </label>

          <div class="block-options">
            @for (template of visibleTemplates(); track template.templateId) {
              <button appUiOptionRow compact class="block-option" type="button" (click)="addTemplate(template)">
                <span appUiOptionRowIcon class="block-option__icon" [ngClass]="'block-option__icon--' + template.kind">
                  <ng-icon [name]="template.icon" size="16" aria-hidden="true" />
                </span>
                <span appUiOptionRowCopy>
                  <strong>{{ template.title }}</strong>
                  <small>{{ template.description }}</small>
                </span>
              </button>
            }
          </div>
        </div>
      </section>
    }
  </section>
</main>
